import React from 'react';
import { Handle, NodeProps, Position } from 'reactflow';
import { twJoin, twMerge } from 'tailwind-merge';

import { Icon, RyeconWarningOutline } from '@dbt-labs/sourdough';

import { GetBoundingBoxFnInternal } from '../dag/dagConstants';
import { getTextBoundingBox } from '../dag/getTextBoundingBox';
import { InteractionDagNode } from '../dag/internalDag';
import { SYSTEM_FONT_FAMILY } from '../dag/styles';
import { DbtDagNode } from '../dbt-dag';
import { BaseNodeComponent } from './BaseNode';

const ERROR_NODE_OFFSET_WIDTH = 50;
const ERROR_NODE_MAX_WIDTH = 260;
const ERROR_NODE_LAYOUT_HEIGHT_PADDING = 60;

const getErrorNodeWidth = (labelWidth: number) => {
  const width = labelWidth + ERROR_NODE_OFFSET_WIDTH;
  return width > ERROR_NODE_MAX_WIDTH ? ERROR_NODE_MAX_WIDTH : width;
};

export const getNodeBoundingBox: GetBoundingBoxFnInternal<any> = (node) => {
  const { label } = node;
  const { width, height: nodeHeight } = getTextBoundingBox(
    label,
    `400 14px ${SYSTEM_FONT_FAMILY}`,
  );
  const nodeWidth = getErrorNodeWidth(width);
  return {
    nodeWidth,
    nodeHeight,
    layoutWidth: nodeWidth,
    layoutHeight: nodeHeight + ERROR_NODE_LAYOUT_HEIGHT_PADDING,
  };
};

const ErrorNodeComponentImplementation = <TDbtDagNode extends DbtDagNode>(
  props: NodeProps<InteractionDagNode<TDbtDagNode>>,
) => {
  const { label, isSelected } = props.data;

  const hoverXOffset = 0;
  const hoverYOffset = 40;

  const border = isSelected
    ? 'box-content border-[2.3px] border-borderMainActive -m-[1px]'
    : 'box-content border-[1px] border-borderMain';

  const baseClassname = twMerge(
    'h-10 cursor-pointer overflow-hidden rounded-md border border-solid',
    border,
    'border-borderDanger',
  );

  const backgroundColorClass = 'bg-bgDangerMuted';
  const textColorClass = 'text-fgDanger';

  return (
    <BaseNodeComponent
      {...props}
      disableHandles
      hoverXOffset={hoverXOffset}
      hoverYOffset={hoverYOffset}
    >
      {({ isMagnified }) => {
        return (
          <div
            className={twMerge(baseClassname, backgroundColorClass)}
            data-resourcetype={props.data.resourceType}
          >
            <Handle
              type="target"
              position={Position.Left}
              isConnectable={false}
              className="pointer-events-none -left-[1px] cursor-pointer opacity-0"
            />
            <div
              className={twMerge(
                'mt-0.5 flex items-center justify-center p-2 text-sm font-normal',
                textColorClass,
              )}
            >
              <Icon className="flex-0 mr-1" ryecon={RyeconWarningOutline} />
              <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
                {label}
              </span>
              <span className="flex-0 ml-2 underline">View Error</span>
            </div>
            <Handle
              type="source"
              isConnectable={false}
              position={Position.Right}
              className="pointer-events-none absolute -right-1 cursor-pointer opacity-0"
            />
            {!isMagnified && (
              <div
                className={twMerge(
                  baseClassname,
                  backgroundColorClass,
                  'absolute inset-0 box-content',
                  'hidden text-5xl',
                  'zoom-level-half:text-3xl',
                  'zoom-level-quarter:block',
                  'zoom-level-half:block',
                )}
              >
                <div className={twJoin('text-center leading-10', 'text-fgDanger')}>
                  ERROR
                </div>
              </div>
            )}
          </div>
        );
      }}
    </BaseNodeComponent>
  );
};

export const ErrorNodeComponent = React.memo(ErrorNodeComponentImplementation);
